import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useHapticFeedback } from '@/shared/hooks/useHapticFeedback';

interface HandlerEntry {
  id: string;
  handler: () => void;
  priority: number;
  timestamp: number;
}

interface BackNavigationContextValue {
  canGoBack: boolean;
  triggerBack: () => void;
  registerHandler: (id: string, handler: () => void, priority?: number) => void;
  unregisterHandler: (id: string) => void;
}

const BackNavigationContext = createContext<BackNavigationContextValue | null>(null);

export function BackNavigationProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { impact } = useHapticFeedback();
  const [handlers, setHandlers] = useState<HandlerEntry[]>([]);
  const handlersRef = useRef<HandlerEntry[]>([]);
  handlersRef.current = handlers;

  const registerHandler = useCallback((id: string, handler: () => void, priority = 0) => {
    setHandlers(prev => {
      const filtered = prev.filter(h => h.id !== id);
      return [...filtered, { id, handler, priority, timestamp: Date.now() }];
    });
  }, []);

  const unregisterHandler = useCallback((id: string) => {
    setHandlers(prev => prev.filter(h => h.id !== id));
  }, []);

  // Находим самый приоритетный (или самый свежий) обработчик
  const activeHandler = handlers.length > 0
    ? [...handlers].sort((a, b) => (b.priority - a.priority) || (b.timestamp - a.timestamp))[0]
    : null;

  // Возможность вернуться назад есть, если зарегистрирован кастомный обработчик
  // либо мы находимся не на главной странице (location.pathname !== '/')
  const canGoBack = Boolean(activeHandler || location.pathname !== '/');

  const triggerBack = useCallback(() => {
    const currentActive = handlersRef.current.length > 0
      ? [...handlersRef.current].sort((a, b) => (b.priority - a.priority) || (b.timestamp - a.timestamp))[0]
      : null;

    if (currentActive) {
      currentActive.handler();
    } else if (location.pathname !== '/') {
      navigate(-1);
    }
  }, [location.pathname, navigate]);

  // Синхронизация с родной кнопкой Telegram BackButton в шапке
  useEffect(() => {
    const tg = (window as any).Telegram?.WebApp;
    if (!tg?.BackButton) return;

    if (canGoBack) {
      tg.BackButton.show();
    } else {
      tg.BackButton.hide();
    }

    const onTgBackClick = () => {
      impact('light');
      triggerBack();
    };

    tg.BackButton.onClick(onTgBackClick);
    return () => {
      tg.BackButton.offClick(onTgBackClick);
    };
  }, [canGoBack, triggerBack, impact]);

  // Отключаем случайное сворачивание Telegram Mini App при свайпах на iOS
  useEffect(() => {
    const tg = (window as any).Telegram?.WebApp;
    if (typeof tg?.disableVerticalSwipes === 'function') {
      tg.disableVerticalSwipes();
    }
  }, []);

  return (
    <BackNavigationContext.Provider value={{ canGoBack, triggerBack, registerHandler, unregisterHandler }}>
      {children}
    </BackNavigationContext.Provider>
  );
}

export function useBackNavigation() {
  const ctx = useContext(BackNavigationContext);
  if (!ctx) {
    throw new Error('useBackNavigation must be used within BackNavigationProvider');
  }
  return ctx;
}

let handlerIdCounter = 0;

/**
 * Хук для регистрации кастомного действия при возврате назад
 * (свайп от левого края экрана или нажатие BackButton Telegram).
 * Если enabled = true, этот обработчик перехватывает возврат.
 */
export function useBackHandler(handler: () => void, enabled = true, priority = 0) {
  const { registerHandler, unregisterHandler } = useBackNavigation();
  const idRef = useRef<string>('');
  if (!idRef.current) {
    idRef.current = `bh-${++handlerIdCounter}`;
  }
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!enabled) {
      unregisterHandler(idRef.current);
      return;
    }

    registerHandler(idRef.current, () => handlerRef.current(), priority);
    return () => {
      unregisterHandler(idRef.current);
    };
  }, [enabled, priority, registerHandler, unregisterHandler]);
}
